import React, { useState, useEffect } from 'react';
import { ArrowLeft, FileText, CheckCircle, AlertCircle, Scale, CreditCard, XCircle, Globe, ChevronDown } from 'lucide-react';
import EgovexLogo from '../components/EgovexLogo';
import { trackPageView } from '../src/lib/analytics';

interface TermsViewProps {
  onBack: () => void;
  lang: 'en' | 'it' | 'es' | 'de' | 'fr';
  onLangChange?: (lang: 'en' | 'it' | 'es' | 'de' | 'fr') => void;
  onHome?: () => void;
}

const TERMS_CONTENT: Record<string, {
  title: string;
  lastUpdated: string;
  intro: string;
  sections: { title: string; icon: any; content: string[] }[];
}> = {
  en: {
    title: 'Terms of Service',
    lastUpdated: 'Last updated: December 2025 | Version 2025.12.1',
    intro: 'By using Egovex, you agree to these terms. Please read them carefully.',
    sections: [
      {
        title: 'What Egovex Is',
        icon: CheckCircle,
        content: [
          'Egovex is an AI-powered personal life companion',
          'It helps you track moments, recognize patterns, and make decisions',
          'The AI provides insights based on your data, not professional advice',
          'Egovex is not a substitute for mental health professionals'
        ]
      },
      {
        title: 'Your Responsibilities',
        icon: Scale,
        content: [
          'You must be at least 16 years old to use Egovex',
          'You are responsible for keeping your account secure',
          'Content you create must not violate any laws',
          'You agree not to misuse the service or attempt to harm others'
        ]
      },
      {
        title: 'Content Ownership',
        icon: FileText,
        content: [
          'You own all content you create in Egovex',
          'You grant us a license to process your content to provide the service',
          'We may use anonymized, aggregated data to improve our AI',
          'We never share your personal content with third parties'
        ]
      },
      {
        title: 'AI Limitations',
        icon: AlertCircle,
        content: [
          'AI insights are suggestions, not definitive guidance',
          'The AI may make mistakes or misinterpret context',
          'Critical life decisions should involve qualified professionals',
          'We continuously improve but cannot guarantee accuracy'
        ]
      },
      {
        title: 'Subscription & Billing',
        icon: CreditCard,
        content: [
          'Free tier includes basic features with usage limits',
          'Premium features require a paid subscription',
          'Subscriptions auto-renew unless cancelled',
          'Refunds are handled according to app store policies'
        ]
      },
      {
        title: 'Termination',
        icon: XCircle,
        content: [
          'You can delete your account at any time',
          'We may suspend accounts that violate these terms',
          'Upon termination, your data will be deleted per our privacy policy',
          'Some anonymized data may be retained for analytics'
        ]
      }
    ]
  },
  it: {
    title: 'Termini di Servizio',
    lastUpdated: 'Ultimo aggiornamento: Dicembre 2025 | Versione 2025.12.1',
    intro: 'Usando Egovex, accetti questi termini. Per favore leggili attentamente.',
    sections: [
      {
        title: 'Cos\'è Egovex',
        icon: CheckCircle,
        content: [
          'Egovex è un compagno di vita personale alimentato da AI',
          'Ti aiuta a tracciare momenti, riconoscere pattern e prendere decisioni',
          'L\'AI fornisce insight basati sui tuoi dati, non consigli professionali',
          'Egovex non sostituisce professionisti della salute mentale'
        ]
      },
      {
        title: 'Le Tue Responsabilità',
        icon: Scale,
        content: [
          'Devi avere almeno 16 anni per usare Egovex',
          'Sei responsabile di mantenere sicuro il tuo account',
          'I contenuti che crei non devono violare alcuna legge',
          'Accetti di non abusare del servizio o tentare di danneggiare altri'
        ]
      },
      {
        title: 'Proprietà dei Contenuti',
        icon: FileText,
        content: [
          'Possiedi tutti i contenuti che crei in Egovex',
          'Ci concedi una licenza per elaborare i tuoi contenuti per fornire il servizio',
          'Possiamo usare dati anonimizzati e aggregati per migliorare la nostra AI',
          'Non condividiamo mai i tuoi contenuti personali con terzi'
        ]
      },
      {
        title: 'Limitazioni AI',
        icon: AlertCircle,
        content: [
          'Gli insight AI sono suggerimenti, non guida definitiva',
          'L\'AI può fare errori o interpretare male il contesto',
          'Decisioni di vita critiche dovrebbero coinvolgere professionisti qualificati',
          'Miglioriamo continuamente ma non possiamo garantire l\'accuratezza'
        ]
      },
      {
        title: 'Abbonamento e Fatturazione',
        icon: CreditCard,
        content: [
          'Il piano gratuito include funzionalità base con limiti di utilizzo',
          'Le funzionalità premium richiedono un abbonamento a pagamento',
          'Gli abbonamenti si rinnovano automaticamente se non cancellati',
          'I rimborsi sono gestiti secondo le politiche degli app store'
        ]
      },
      {
        title: 'Terminazione',
        icon: XCircle,
        content: [
          'Puoi eliminare il tuo account in qualsiasi momento',
          'Possiamo sospendere account che violano questi termini',
          'Alla terminazione, i tuoi dati saranno eliminati secondo la nostra privacy policy',
          'Alcuni dati anonimizzati possono essere mantenuti per analytics'
        ]
      }
    ]
  },
  es: {
    title: 'Términos de Servicio',
    lastUpdated: 'Última actualización: Diciembre 2025 | Versión 2025.12.1',
    intro: 'Al usar Egovex, aceptas estos términos. Por favor léelos cuidadosamente.',
    sections: [
      {
        title: 'Qué es Egovex',
        icon: CheckCircle,
        content: [
          'Egovex es un compañero de vida personal impulsado por IA',
          'Te ayuda a rastrear momentos, reconocer patrones y tomar decisiones',
          'La IA proporciona insights basados en tus datos, no consejos profesionales',
          'Egovex no sustituye a profesionales de salud mental'
        ]
      },
      {
        title: 'Tus Responsabilidades',
        icon: Scale,
        content: [
          'Debes tener al menos 16 años para usar Egovex',
          'Eres responsable de mantener tu cuenta segura',
          'El contenido que crees no debe violar ninguna ley',
          'Aceptas no abusar del servicio o intentar dañar a otros'
        ]
      },
      {
        title: 'Propiedad del Contenido',
        icon: FileText,
        content: [
          'Eres dueño de todo el contenido que creas en Egovex',
          'Nos otorgas una licencia para procesar tu contenido para proporcionar el servicio',
          'Podemos usar datos anonimizados y agregados para mejorar nuestra IA',
          'Nunca compartimos tu contenido personal con terceros'
        ]
      },
      {
        title: 'Limitaciones de la IA',
        icon: AlertCircle,
        content: [
          'Los insights de IA son sugerencias, no guía definitiva',
          'La IA puede cometer errores o malinterpretar el contexto',
          'Las decisiones críticas deben involucrar profesionales calificados',
          'Mejoramos continuamente pero no podemos garantizar precisión'
        ]
      },
      {
        title: 'Suscripción y Facturación',
        icon: CreditCard,
        content: [
          'El plan gratuito incluye funciones básicas con límites de uso',
          'Las funciones premium requieren una suscripción pagada',
          'Las suscripciones se renuevan automáticamente si no se cancelan',
          'Los reembolsos se manejan según las políticas de las tiendas de apps'
        ]
      },
      {
        title: 'Terminación',
        icon: XCircle,
        content: [
          'Puedes eliminar tu cuenta en cualquier momento',
          'Podemos suspender cuentas que violen estos términos',
          'Al terminar, tus datos serán eliminados según nuestra política de privacidad',
          'Algunos datos anonimizados pueden retenerse para análisis'
        ]
      }
    ]
  },
  de: {
    title: 'Nutzungsbedingungen',
    lastUpdated: 'Zuletzt aktualisiert: Dezember 2025 | Version 2025.12.1',
    intro: 'Durch die Nutzung von Egovex stimmen Sie diesen Bedingungen zu. Bitte lesen Sie sie sorgfältig.',
    sections: [
      {
        title: 'Was Egovex ist',
        icon: CheckCircle,
        content: [
          'Egovex ist ein KI-gesteuerter persönlicher Lebensbegleiter',
          'Es hilft Ihnen, Momente zu verfolgen, Muster zu erkennen und Entscheidungen zu treffen',
          'Die KI liefert Einblicke basierend auf Ihren Daten, keine professionelle Beratung',
          'Egovex ersetzt keine Fachleute für psychische Gesundheit'
        ]
      },
      {
        title: 'Ihre Verantwortlichkeiten',
        icon: Scale,
        content: [
          'Sie müssen mindestens 16 Jahre alt sein, um Egovex zu nutzen',
          'Sie sind für die Sicherheit Ihres Kontos verantwortlich',
          'Von Ihnen erstellte Inhalte dürfen keine Gesetze verletzen',
          'Sie stimmen zu, den Dienst nicht zu missbrauchen oder anderen zu schaden'
        ]
      },
      {
        title: 'Eigentum an Inhalten',
        icon: FileText,
        content: [
          'Sie besitzen alle Inhalte, die Sie in Egovex erstellen',
          'Sie gewähren uns eine Lizenz zur Verarbeitung Ihrer Inhalte',
          'Wir können anonymisierte Daten zur Verbesserung unserer KI verwenden',
          'Wir teilen Ihre persönlichen Inhalte niemals mit Dritten'
        ]
      },
      {
        title: 'KI-Einschränkungen',
        icon: AlertCircle,
        content: [
          'KI-Einblicke sind Vorschläge, keine definitive Anleitung',
          'Die KI kann Fehler machen oder den Kontext falsch interpretieren',
          'Kritische Lebensentscheidungen sollten qualifizierte Fachleute einbeziehen',
          'Wir verbessern uns ständig, können aber keine Genauigkeit garantieren'
        ]
      },
      {
        title: 'Abonnement und Abrechnung',
        icon: CreditCard,
        content: [
          'Die kostenlose Stufe umfasst Grundfunktionen mit Nutzungslimits',
          'Premium-Funktionen erfordern ein kostenpflichtiges Abonnement',
          'Abonnements verlängern sich automatisch, wenn sie nicht gekündigt werden',
          'Rückerstattungen werden gemäß den App-Store-Richtlinien gehandhabt'
        ]
      },
      {
        title: 'Kündigung',
        icon: XCircle,
        content: [
          'Sie können Ihr Konto jederzeit löschen',
          'Wir können Konten sperren, die gegen diese Bedingungen verstoßen',
          'Bei Kündigung werden Ihre Daten gemäß unserer Datenschutzrichtlinie gelöscht',
          'Einige anonymisierte Daten können für Analysen aufbewahrt werden'
        ]
      }
    ]
  },
  fr: {
    title: 'Conditions d\'Utilisation',
    lastUpdated: 'Dernière mise à jour: Décembre 2025 | Version 2025.12.1',
    intro: 'En utilisant Egovex, vous acceptez ces conditions. Veuillez les lire attentivement.',
    sections: [
      {
        title: 'Qu\'est-ce qu\'Egovex',
        icon: CheckCircle,
        content: [
          'Egovex est un compagnon de vie personnel alimenté par l\'IA',
          'Il vous aide à suivre les moments, reconnaître les schémas et prendre des décisions',
          'L\'IA fournit des insights basés sur vos données, pas des conseils professionnels',
          'Egovex ne remplace pas les professionnels de la santé mentale'
        ]
      },
      {
        title: 'Vos Responsabilités',
        icon: Scale,
        content: [
          'Vous devez avoir au moins 16 ans pour utiliser Egovex',
          'Vous êtes responsable de la sécurité de votre compte',
          'Le contenu que vous créez ne doit violer aucune loi',
          'Vous acceptez de ne pas abuser du service ou de nuire aux autres'
        ]
      },
      {
        title: 'Propriété du Contenu',
        icon: FileText,
        content: [
          'Vous possédez tout le contenu que vous créez dans Egovex',
          'Vous nous accordez une licence pour traiter votre contenu',
          'Nous pouvons utiliser des données anonymisées pour améliorer notre IA',
          'Nous ne partageons jamais votre contenu personnel avec des tiers'
        ]
      },
      {
        title: 'Limitations de l\'IA',
        icon: AlertCircle,
        content: [
          'Les insights IA sont des suggestions, pas des conseils définitifs',
          'L\'IA peut faire des erreurs ou mal interpréter le contexte',
          'Les décisions critiques doivent impliquer des professionnels qualifiés',
          'Nous nous améliorons continuellement mais ne pouvons garantir la précision'
        ]
      },
      {
        title: 'Abonnement et Facturation',
        icon: CreditCard,
        content: [
          'Le niveau gratuit inclut des fonctionnalités de base avec des limites',
          'Les fonctionnalités premium nécessitent un abonnement payant',
          'Les abonnements se renouvellent automatiquement sauf annulation',
          'Les remboursements sont gérés selon les politiques des stores'
        ]
      },
      {
        title: 'Résiliation',
        icon: XCircle,
        content: [
          'Vous pouvez supprimer votre compte à tout moment',
          'Nous pouvons suspendre les comptes qui violent ces conditions',
          'À la résiliation, vos données seront supprimées selon notre politique',
          'Certaines données anonymisées peuvent être conservées pour l\'analyse'
        ]
      }
    ]
  }
};

const TermsView: React.FC<TermsViewProps> = ({ onBack, lang, onLangChange, onHome }) => {
  const content = TERMS_CONTENT[lang] || TERMS_CONTENT.en;
  const [showLangMenu, setShowLangMenu] = useState(false);

  useEffect(() => {
    trackPageView('/terms', 'Terms of Service', false);
  }, []);

  const languageLabels: Record<string, string> = {
    en: 'English',
    it: 'Italiano',
    es: 'Español',
    de: 'Deutsch',
    fr: 'Français'
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/[0.04]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onHome || onBack}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <EgovexLogo size={32} />
              <span className="font-black text-lg tracking-tighter hidden sm:inline">Egovex</span>
            </button>
            <div className="h-6 w-px bg-black/10 hidden sm:block" />
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-sm font-bold text-black/60 hover:text-black transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">
                {lang === 'it' ? 'Indietro' : lang === 'es' ? 'Atrás' : lang === 'de' ? 'Zurück' : lang === 'fr' ? 'Retour' : 'Back'}
              </span>
            </button>
          </div>
          
          {onLangChange && (
            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-black/10 hover:border-black/20 transition-colors text-sm font-medium"
              >
                <Globe className="w-4 h-4 text-black/40" />
                <span className="hidden sm:inline">{languageLabels[lang]}</span>
                <ChevronDown className={`w-3 h-3 text-black/40 transition-transform ${showLangMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showLangMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowLangMenu(false)} />
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-black/10 py-2 z-50 min-w-[140px]">
                    {(['en', 'it', 'es', 'de', 'fr'] as const).map((l) => (
                      <button
                        key={l}
                        onClick={() => { onLangChange(l); setShowLangMenu(false); }}
                        className={`w-full px-4 py-2 text-left text-sm font-medium hover:bg-black/5 transition-colors ${lang === l ? 'text-indigo-600 bg-indigo-50' : 'text-black/70'}`}
                      >
                        {languageLabels[l]}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-full mb-4">
            <FileText className="w-4 h-4 text-indigo-600" />
            <span className="text-[11px] font-black uppercase tracking-widest text-indigo-600">
              {lang === 'it' ? 'Termini legali' : 'Legal Terms'}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-[#1D1D1F] mb-3">
            {content.title}
          </h1>
          <p className="text-black/40 font-medium">{content.lastUpdated}</p>
        </div>

        <p className="text-lg text-black/60 leading-relaxed mb-12 font-medium">
          {content.intro}
        </p>

        <div className="space-y-12">
          {content.sections.map((section, idx) => (
            <div key={idx} className="bg-[#FAFAFA] rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-indigo-600" />
                </div>
                <h2 className="text-xl font-black tracking-tight text-[#1D1D1F]">
                  {section.title}
                </h2>
              </div>
              <ul className="space-y-3">
                {section.content.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-black/60 font-medium">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default TermsView;
